import React, { useMemo, useState } from 'react';
import { useQuery } from '@apollo/client';
import {
  TrendingUp,
  Users,
  ShoppingBag,
  ArrowUpRight,
  ArrowDownRight,
  Clock,
  AlertCircle,
  Loader2,
} from 'lucide-react';
import {
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  AreaChart,
  Area,
} from 'recharts';
import { motion } from 'motion/react';

import {
  GET_DASHBOARD_ACTIVE_FSRS,
  GET_DASHBOARD_KPIS,
  GET_DASHBOARD_RECENT_BOOKINGS,
  GET_DASHBOARD_SALES_TREND,
  GET_DASHBOARD_STATUS_BREAKDOWN,
} from '../graphql/queries';

interface Props {
  portal: string;
  onNavigate: (page: string) => void;
}

export const Dashboard: React.FC<Props> = ({ portal, onNavigate }) => {
  const isMfr = portal === 'manufacturer';
  const portalPrefix = isMfr ? 'mfr' : 'dist';

  const [range, setRange] = useState<'week' | 'month'>('week');

  const { data: kpiData, loading: kpiLoading } = useQuery(GET_DASHBOARD_KPIS, {
    fetchPolicy: 'network-only',
    pollInterval: 60_000,
  });
  const { data: trendData, loading: trendLoading } = useQuery(GET_DASHBOARD_SALES_TREND, {
    variables: { range },
    fetchPolicy: 'network-only',
  });
  const { data: statusData } = useQuery(GET_DASHBOARD_STATUS_BREAKDOWN, {
    fetchPolicy: 'network-only',
    pollInterval: 60_000,
  });
  const { data: fsrsData } = useQuery(GET_DASHBOARD_ACTIVE_FSRS, {
    variables: { limit: 5 },
    fetchPolicy: 'network-only',
  });
  const { data: recentData } = useQuery(GET_DASHBOARD_RECENT_BOOKINGS, {
    variables: { limit: 6 },
    fetchPolicy: 'network-only',
    pollInterval: 60_000,
  });

  const kpis = kpiData?.getDashboardKpis;
  const trend = trendData?.getDashboardSalesTrend?.points || [];
  const breakdown = statusData?.getDashboardStatusBreakdown;
  const fsrs = fsrsData?.getDashboardActiveFsrs || [];
  const recent = recentData?.getDashboardRecentBookings || [];

  const kpiCards = useMemo(() => {
    if (!kpis) return [];
    return [
      {
        label: "Today's Revenue",
        value: fmtCompactInr(kpis.revenueToday || 0),
        trend: kpis.revenueDelta,
        icon: TrendingUp,
        color: 'text-primary',
        bg: 'bg-indigo-50',
      },
      {
        label: 'Active Field Reps',
        value: `${kpis.activeFsrs || 0} / ${kpis.totalFsrs || 0}`,
        trend: null,
        icon: Users,
        color: 'text-emerald-600',
        bg: 'bg-emerald-50',
      },
      {
        label: 'Bookings Today',
        value: `${kpis.bookingsToday || 0}`,
        trend: kpis.bookingsDelta,
        icon: ShoppingBag,
        color: 'text-blue-600',
        bg: 'bg-blue-50',
      },
      {
        label: 'Avg Order Value',
        value: fmtInr(Math.round(kpis.avgOrderValueToday || 0)),
        trend: kpis.avgDelta,
        icon: Clock,
        color: 'text-amber-600',
        bg: 'bg-amber-50',
      },
    ];
  }, [kpis]);

  const pieData = useMemo(() => {
    const items = breakdown?.items || [];
    const palette: Record<string, string> = {
      pending: '#F59E0B',
      approved: '#10B981',
      dispatched: '#3B82F6',
      delivered: '#14B8A6',
      rejected: '#EF4444',
      cancelled: '#94A3B8',
      fulfilled: '#6366F1',
    };
    return items
      .filter((i: any) => i.count > 0)
      .map((i: any) => ({
        name: i.status[0].toUpperCase() + i.status.slice(1),
        value: i.count,
        color: palette[i.status] || '#94A3B8',
      }));
  }, [breakdown]);

  const pendingCount = breakdown?.items?.find((i: any) => i.status === 'pending')?.count ?? 0;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-black text-text">Dashboard</h1>
          <p className="text-text2">Live snapshot of your field team — refreshed every minute.</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 px-3 py-1.5 bg-emerald-50 border border-emerald-100 rounded-full">
            <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
            <span className="text-[10px] font-bold text-emerald-700 uppercase tracking-tight">
              Live
            </span>
          </div>
        </div>
      </div>

      {pendingCount > 0 && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-amber-50 border border-amber-200 rounded-2xl p-4 flex items-center justify-between"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center text-amber-600">
              <AlertCircle size={20} />
            </div>
            <div>
              <h3 className="font-bold text-amber-900 leading-tight">
                ⚡ {pendingCount} booking{pendingCount === 1 ? '' : 's'} need{pendingCount === 1 ? 's' : ''} approval
              </h3>
              <p className="text-xs text-amber-700 opacity-80">
                Field reps are waiting on your review before fulfilment can move forward.
              </p>
            </div>
          </div>
          <button
            onClick={() => onNavigate(`${portalPrefix}-bookings`)}
            className="px-4 py-2 bg-amber-600 text-white text-xs font-bold rounded-lg shadow-lg shadow-amber-600/20 hover:bg-amber-700 transition-all"
          >
            Review All
          </button>
        </motion.div>
      )}

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {kpiLoading && kpiCards.length === 0
          ? Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="card p-5 h-32 bg-gray-50 animate-pulse rounded-xl" />
            ))
          : kpiCards.map((kpi, i) => (
              <div key={i} className="card p-5 flex flex-col">
                <div className="flex justify-between items-start mb-4">
                  <div
                    className={`w-10 h-10 rounded-xl ${kpi.bg} flex items-center justify-center ${kpi.color}`}
                  >
                    <kpi.icon size={20} />
                  </div>
                  {kpi.trend !== null && (
                    <DeltaPill value={kpi.trend as number} />
                  )}
                </div>
                <span className="text-[10px] font-black text-text3 uppercase tracking-widest mb-1">
                  {kpi.label}
                </span>
                <span className="text-2xl font-black text-text">{kpi.value}</span>
              </div>
            ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Sales Chart */}
        <div className="lg:col-span-2 card p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-black text-text">Sales Performance</h2>
            <div className="flex p-1 bg-gray-100 rounded-lg">
              <button
                onClick={() => setRange('week')}
                className={`px-3 py-1.5 rounded-md text-[10px] font-bold ${
                  range === 'week' ? 'bg-white shadow-sm text-text' : 'text-text3 hover:text-text'
                }`}
              >
                Weekly
              </button>
              <button
                onClick={() => setRange('month')}
                className={`px-3 py-1.5 rounded-md text-[10px] font-bold ${
                  range === 'month' ? 'bg-white shadow-sm text-text' : 'text-text3 hover:text-text'
                }`}
              >
                Monthly
              </button>
            </div>
          </div>
          <div className="h-[300px] w-full relative">
            {trendLoading && trend.length === 0 && (
              <div className="absolute inset-0 flex items-center justify-center text-text3">
                <Loader2 className="animate-spin" />
              </div>
            )}
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={trend}>
                <defs>
                  <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#4F46E5" stopOpacity={0.18} />
                    <stop offset="95%" stopColor="#4F46E5" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#F1F5F9" />
                <XAxis
                  dataKey="label"
                  axisLine={false}
                  tickLine={false}
                  tick={{ fontSize: 11, fontWeight: 600, fill: '#94A3B8' }}
                  dy={10}
                />
                <YAxis
                  axisLine={false}
                  tickLine={false}
                  tick={{ fontSize: 11, fontWeight: 600, fill: '#94A3B8' }}
                  tickFormatter={(v) => fmtCompactInr(v)}
                />
                <Tooltip
                  formatter={(v: number) => [fmtInr(v), 'Revenue']}
                  contentStyle={{
                    borderRadius: '16px',
                    border: 'none',
                    boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
                    fontWeight: 700,
                  }}
                />
                <Area
                  type="monotone"
                  dataKey="revenue"
                  stroke="#4F46E5"
                  strokeWidth={3}
                  fillOpacity={1}
                  fill="url(#colorRevenue)"
                  animationDuration={800}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Status Pie */}
        <div className="card p-6">
          <h2 className="font-black text-text mb-6">Booking Status</h2>
          {pieData.length === 0 ? (
            <div className="h-[240px] flex items-center justify-center text-text3 text-sm font-bold">
              No bookings yet.
            </div>
          ) : (
            <>
              <div className="h-[240px] w-full relative">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={pieData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={80}
                      paddingAngle={6}
                      dataKey="value"
                      animationDuration={800}
                    >
                      {pieData.map((e: any, i: number) => (
                        <Cell key={i} fill={e.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
                <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                  <span className="text-3xl font-black text-text">{breakdown?.total ?? 0}</span>
                  <span className="text-[10px] font-bold text-text3 uppercase tracking-widest">
                    Total
                  </span>
                </div>
              </div>
              <div className="space-y-2 mt-4">
                {pieData.map((item: any, i: number) => (
                  <div key={i} className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div
                        className="w-2 h-2 rounded-full"
                        style={{ backgroundColor: item.color }}
                      />
                      <span className="text-xs font-bold text-text2">{item.name}</span>
                    </div>
                    <span className="text-xs font-black text-text">{item.value}</span>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Recent Bookings */}
        <div className="lg:col-span-2 card overflow-hidden">
          <div className="p-4 border-b border-border flex justify-between items-center">
            <h2 className="font-black text-text">Recent Bookings</h2>
            <button
              onClick={() => onNavigate(`${portalPrefix}-bookings`)}
              className="text-primary text-xs font-bold hover:underline"
            >
              View All
            </button>
          </div>
          {recent.length === 0 ? (
            <div className="p-12 text-center text-text3 text-sm font-bold">
              No bookings yet — they'll show up here as field reps submit them.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead className="bg-gray-50 text-[10px] font-black text-text3 uppercase tracking-widest">
                  <tr>
                    <th className="px-6 py-3">Retailer</th>
                    <th className="px-6 py-3">Rep</th>
                    <th className="px-6 py-3">Value</th>
                    <th className="px-6 py-3">Status</th>
                    <th className="px-6 py-3 text-right">When</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {recent.map((b: any) => (
                    <tr key={b.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4">
                        <div className="flex flex-col">
                          <span className="font-bold text-text">
                            {b.retailerName || b.bookingNumber}
                          </span>
                          {b.retailerCity && (
                            <span className="text-[10px] text-text3 font-bold uppercase">
                              {b.retailerCity}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <div className="w-6 h-6 rounded-lg bg-primary/10 text-primary flex items-center justify-center text-[8px] font-black">
                            {initials(b.fsrName || '')}
                          </div>
                          <span className="text-xs font-medium text-text2">
                            {b.fsrName || b.fsrId}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 font-bold text-text">{fmtInr(b.total)}</td>
                      <td className="px-6 py-4">
                        <StatusPill status={b.status} />
                      </td>
                      <td className="px-6 py-4 text-right text-text3 text-xs font-medium">
                        {fmtRelative(b.submittedAt)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Active Reps */}
        <div className="card p-6">
          <h2 className="font-black text-text mb-6">Active Reps Today</h2>
          {fsrs.length === 0 ? (
            <div className="text-text3 text-sm font-bold">
              No reps have started today's beat yet.
            </div>
          ) : (
            <div className="space-y-5">
              {fsrs.map((u: any) => (
                <div key={u.fsrId} className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div
                      className="w-10 h-10 rounded-xl flex items-center justify-center text-white font-bold text-xs"
                      style={{ backgroundColor: u.color || '#4F46E5' }}
                    >
                      {initials(u.name)}
                    </div>
                    <div className="flex flex-col">
                      <span className="text-sm font-bold text-text leading-tight">{u.name}</span>
                      <span className="text-[10px] text-text3 font-bold uppercase tracking-tight">
                        {u.employeeId}
                      </span>
                    </div>
                  </div>
                  <div className="flex flex-col items-end">
                    <div className="flex items-center gap-1.5">
                      <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                      <span className="text-[10px] font-bold text-emerald-600 uppercase">Live</span>
                    </div>
                    <span className="text-[10px] text-text3 font-bold">
                      {u.todayDone} / {u.todayPlan} visits
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
          <button
            onClick={() => onNavigate(`${portalPrefix}-fa-tracker`)}
            className="w-full mt-8 h-10 bg-gray-50 border border-border rounded-xl text-xs font-bold text-text2 hover:bg-gray-100 transition-all"
          >
            View Tracking Map
          </button>
        </div>
      </div>
    </div>
  );
};

const DeltaPill: React.FC<{ value: number }> = ({ value }) => {
  if (value === 0) {
    return (
      <div className="flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-600">
        — 0%
      </div>
    );
  }
  const isUp = value > 0;
  return (
    <div
      className={`flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded-full ${
        isUp ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'
      }`}
    >
      {isUp ? <ArrowUpRight size={10} /> : <ArrowDownRight size={10} />}
      {Math.abs(value).toFixed(1)}%
    </div>
  );
};

const StatusPill: React.FC<{ status: string }> = ({ status }) => {
  const map: Record<string, string> = {
    approved: 'bg-emerald-100 text-emerald-700',
    pending: 'bg-amber-100 text-amber-700',
    dispatched: 'bg-blue-100 text-blue-700',
    delivered: 'bg-teal-100 text-teal-700',
    rejected: 'bg-red-100 text-red-700',
    cancelled: 'bg-gray-200 text-gray-700',
    fulfilled: 'bg-indigo-100 text-indigo-700',
  };
  return (
    <span
      className={`px-2 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-tight ${
        map[status] || 'bg-gray-100 text-gray-700'
      }`}
    >
      {status}
    </span>
  );
};

function initials(name: string) {
  if (!name) return '··';
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function fmtInr(n: number) {
  return `₹${Math.round(n || 0).toLocaleString('en-IN')}`;
}

function fmtCompactInr(n: number) {
  if (!n) return '₹0';
  if (n >= 1e7) return `₹${(n / 1e7).toFixed(1).replace(/\.0$/, '')}Cr`;
  if (n >= 1e5) return `₹${(n / 1e5).toFixed(1).replace(/\.0$/, '')}L`;
  if (n >= 1e3) return `₹${(n / 1e3).toFixed(1).replace(/\.0$/, '')}k`;
  return `₹${Math.round(n).toLocaleString('en-IN')}`;
}

function fmtRelative(iso: string | null) {
  if (!iso) return '—';
  const dt = new Date(iso);
  if (Number.isNaN(dt.getTime())) return '—';
  const diff = Date.now() - dt.getTime();
  const minutes = Math.floor(diff / 60_000);
  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return dt.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
}
